import { useEffect, useState } from "react";

export interface StarCounterProps {
  count: number;
  size?: "sm" | "md" | "lg";
  animate?: boolean;
  label?: string;
}

/**
 * Animated star counter with sparkle effects.
 * Shows total stars with a playful, celebratory design.
 */
export function StarCounter({
  count,
  size = "md",
  animate = true,
  label = "Stars",
}: StarCounterProps) {
  const [displayCount, setDisplayCount] = useState(animate ? 0 : count);

  // Animate count up
  useEffect(() => {
    if (!animate) {
      setDisplayCount(count);
      return;
    }

    if (count === 0) {
      setDisplayCount(0);
      return;
    }

    const duration = 1200; // ms
    const steps = Math.min(count, 30);
    const stepDuration = duration / steps;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayCount(Math.round(count * eased));

      if (currentStep >= steps) {
        clearInterval(timer);
        setDisplayCount(count);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [count, animate]);

  const sizeConfig = {
    sm: {
      container: "gap-1.5",
      star: "text-2xl",
      count: "text-xl",
      label: "text-xs",
    },
    md: {
      container: "gap-2",
      star: "text-4xl",
      count: "text-3xl",
      label: "text-sm",
    },
    lg: {
      container: "gap-3",
      star: "text-6xl",
      count: "text-5xl",
      label: "text-base",
    },
  };

  const config = sizeConfig[size];

  return (
    <div className={`flex items-center ${config.container}`}>
      {/* Animated star icon */}
      <div className="relative">
        <span
          className={`${config.star} inline-block animate-float`}
          role="img"
          aria-hidden="true"
        >
          ⭐
        </span>
        {/* Sparkle decorations */}
        {count > 0 && (
          <>
            <span
              className="absolute -top-1 -right-1 text-xs opacity-80 animate-sparkle"
              style={{ animationDelay: "0ms" }}
            >
              ✨
            </span>
            <span
              className="absolute -bottom-0.5 -left-1 text-[10px] opacity-60 animate-sparkle"
              style={{ animationDelay: "300ms" }}
            >
              ✨
            </span>
          </>
        )}
      </div>

      {/* Count display */}
      <div className="flex flex-col">
        <span
          className={`font-display font-extrabold ${config.count} text-sunshine-600 leading-none`}
        >
          {displayCount.toLocaleString()}
        </span>
        <span className={`font-body font-medium ${config.label} text-neutral-500`}>
          {label}
        </span>
      </div>
    </div>
  );
}

export default StarCounter;
